import { ChangeEvent } from '@faro/realtime-services';
import { useCallback } from 'react';
import { useInactiveQueryInvalidation } from '../../../package/react-query/useInactiveQueryInvalidation';
import { useServiceContext } from '../service/ServiceContext';

/**
 * Registry for non-factory pattern query keys that should be invalidated
 * when change events occur for a study. Registered keys follow the pattern
 * [queryKeyPrefix, studyId, ...] and will be invalidated when any change
 * event matches the studyId scope.
 *
 * Usage: Call registerStudyQueryKey('myQueryKey') at module load time.
 */
const registeredQueryKeys = new Set<string>();

/**
 * Register a query key prefix for study-scoped invalidation.
 * Call this at module load time (outside of hooks/components).
 *
 * @example
 * // In MyFeatureHooks.ts
 * registerStudyQueryKey('myFeature');
 *
 * export function useMyFeature(studyId: string) {
 *   return useQuery({ queryKey: ['myFeature', studyId, ...], ... });
 * }
 */
export function registerStudyQueryKey(queryKeyPrefix: string): void {
    registeredQueryKeys.add(queryKeyPrefix);
}

/**
 * Listens to change events and invalidates inactive (unmounted) queries
 * that match the study scope. This solves the stale data problem when
 * navigating between views like Insights and SoA.
 *
 * Active queries (with mounted observers) are NOT invalidated here because
 * they have their own event listeners via useRealtimeQuery.
 */
export interface UseStudyInactiveQueryInvalidationOptions {
    enabled: boolean;
}

export function useStudyInactiveQueryInvalidation(
    studyId: string,
    options: UseStudyInactiveQueryInvalidationOptions
): void {
    const { changeEventService } = useServiceContext();
    const { enabled } = options;

    const shouldInvalidate = useCallback(
        (queryKey: readonly unknown[], changeEvents: ChangeEvent<any>[]): boolean => {
            // Factory pattern: ['study', studyId, entityType, params]
            if (queryKey[0] === 'study' && queryKey[1] === studyId) {
                const entityType = queryKey[2];
                return changeEvents.some(e => e.scope === studyId && e.type === entityType);
            }

            // Registered query keys: [queryKeyPrefix, studyId, ...]
            if (registeredQueryKeys.has(queryKey[0] as string) && queryKey[1] === studyId) {
                return changeEvents.some(e => e.scope === studyId);
            }

            return false;
        },
        [studyId]
    );

    useInactiveQueryInvalidation({
        events: changeEventService.events,
        shouldInvalidate,
        enabled,
    });
}
